import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getVoxTestCases,
  getSupportedVendors,
  createVoxTestCase,
  updateVoxTestCase,
  deleteVoxTestCase,
} from '../services/voxTestCaseService';
import { CreateOrUpdateVoxTestCaseViewModel, VoxTestCaseViewModel } from '../models/voxTestCase';

export const useVoxTestCases = (vendor?: string, activeOnly = false) => {
  return useQuery<VoxTestCaseViewModel[]>({
    queryKey: ['vox-test-cases', vendor ?? null, activeOnly],
    queryFn: () => getVoxTestCases(vendor, activeOnly),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
};

export const useSupportedVoxVendors = () => {
  return useQuery<string[]>({
    queryKey: ['vox-test-case-vendors'],
    queryFn: getSupportedVendors,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useVoxTestCaseMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vox-test-cases'] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateVoxTestCaseViewModel) => createVoxTestCase(payload),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ voxTestCaseId, payload }: { voxTestCaseId: number; payload: CreateOrUpdateVoxTestCaseViewModel }) =>
      updateVoxTestCase(voxTestCaseId, payload),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (voxTestCaseId: number) => deleteVoxTestCase(voxTestCaseId),
    onSuccess: invalidate,
  });

  return {
    createTestCase: createMutation.mutateAsync,
    updateTestCase: updateMutation.mutateAsync,
    deleteTestCase: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
  };
};
